/* eslint-disable no-param-reassign */
import d3 from 'd3';
import * as Config from 'es6/components/memory-bank/config';
import Tooltip from 'es6/components/memory-bank/tooltip';

/**
 * BaseVisual for the different visuals the memory bank uses.
 */
export class BaseVisual {
  constructor(memoryBankPlot) {
    this._memoryBankPlot = memoryBankPlot;
  }

  /**
   * Sets up the entering nodes, exiting nodes and gives an opportunity
   * to style the attributes. It is not really recommended to override
   * this method, but the other methods if changes are needed.
   */
  _beforeShowOrUpdate() {
    const selection = this._memoryBankPlot.selection;

    this._handleEnterNodes(selection.enter());
    selection.each(this._setDefaultAttributes);
    this._handleExitNodes(selection.exit());
  }

  /**
   * When given an array of MemoryBankNodes, will return a d3
   * object of the associated nodes.
   *
   * @param {Array}           memoryBankNodes
   * @return {d3_selection}   a D3 selection object.
   */
  _getSelection(memoryBankNodes) {
    const set = new Set(memoryBankNodes);
    return this._memoryBankPlot.nodesContainer.selectAll('g').filter(d => set.has(d));
  }

  /**
   * Retrieves the 'visible' nodes from the selection.
   *
   * @return {d3_selection}  the visible nodes in the plot data
   */
  _getVisible() {
    return this._memoryBankPlot.nodesContainer.selectAll('g').filter(this._isNodeVisible);
  }

  /**
   * Method that handles where entered nodes should start. Have them
   * start to the bottom left of the visual.
   *
   * @params {d3_selection} selection  The d3 data of the nodes entering
   */
  _handleEnterNodes(selection) {
    const enteredGroupNodes = selection.append('g')
      .attr('transform', this._hiddenCoords);

    enteredGroupNodes.append('circle')
      .classed('node-circle', true)
      .attr('r', Config.Circle.RADIUS);

    enteredGroupNodes.append('text')
      .classed('node-group-count-badge', true)
      .attr('text-anchor', 'middle');
  }

  /**
   * Method that handles what should happen to exiting nodes.
   *
   * @params {d3_selection} selection  The d3 data of the nodes exiting
   */
  _handleExitNodes(selection) {
    selection.transition()
      .duration(Config.Animation.SLOWEST)
      .attr('transform', this._hiddenCoords)
      .each('end', function () {
        if (this.parentNode) {
          this.parentNode.removeChild(this);
        }
      });
  }

  /**
   * Returns coordinates for where to hide nodes.
   *
   * @returns {String}
   */
  get _hiddenCoords() {
    return this._translate(Config.HiddenCoords.X, Config.HiddenCoords.Y);
  }

  /**
   * Unbind the tooltip events.
   */
  hide() {
    this._memoryBankPlot.selection.on({
      'mouseenter.tooltip': null,
      'mouseleave.tooltip': null,
    });
  }

  /**
   * Move nodes that aren't visible off the plot.
   */
  _moveHiddenNodes() {
    this._memoryBankPlot.selection
      .filter(d => !this._isNodeVisible(d))
      .transition()
      .duration(Config.Animation.SLOWEST)
      .delay(d => Math.random() * 200)
      .attr('transform', this._hiddenCoords)
      .each(d => {
        d.current = {
          x: Config.HiddenCoords.X,
          y: Config.HiddenCoords.Y,
        };
      });
  }

  /**
   * Helper method to determine if the node should be shown on
   * the visuals.
   *
   * @param {MemoryBankNode} node
   * @returns {Boolean}
   */
  _isNodeVisible(node) {
    return !node.skipped && !node.isNew();
  }

  /**
   * A callback placed on all the nodes that can be used to set attributes.
   *
   * @this  The HTMLElement associated with the node.
   * @param {MemoryBankNode} node
   */
  _setDefaultAttributes(node) {
    const _this = d3.select(this);

    const entered = !_this.classed('node');
    const wasNew = _this.classed('new') && !node.isNew();

    _this.classed({
      'checkpoint-1': node.isAtCheckpoint1(),
      'checkpoint-2': node.isAtCheckpoint2(),
      'checkpoint-3': node.isAtCheckpoint3(),
      entered,
      'in-session': node.inSession,
      locked: node.shortTermMemory && node.isReady(),
      new: node.isNew(),
      node: 'true',
      'not-ready': !node.isReady(),
      ready: node.isReady(),
      skipped: node.skipped,
      unstarted: node.isNew(),
      'was-new': wasNew,
    });
  }

  /**
   * Applies the tooltips to the nodes on the memory bank.
   */
  _setTooltips() {
    this._memoryBankPlot.selection.on({
      'mouseenter.tooltip': Tooltip.create,
      'mouseleave.tooltip': Tooltip.remove,
    });
  }

  /**
   * Called when a visual is either initially rendered or transitioning
   * from another visual. The inheriting class should define what happens
   * to the updated nodes.
   */
  show() {
    this._beforeShowOrUpdate();
  }

  /**
   * Returns a translate string for the transform attribute.
   *
   * @params {number} x
   * @params {number} y
   * @return {string}
   */
  _translate(x, y) {
    return `translate(${x}, ${y})`;
  }

  /**
   * Called when a visual is being updated. In this case, the nodes may
   * be changing position.
   */
  update() {
    this._beforeShowOrUpdate();
  }
}

export default BaseVisual;
